import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { BarChart3, Loader2, TrendingUp, Smile, AlertCircle, Calendar } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { API, useAuth } from "@/App";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, Legend
} from "recharts";

const Analytics = () => {
  const { token } = useAuth();
  const [moodData, setMoodData] = useState(null);
  const [cyclePatterns, setCyclePatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState("3");

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const COLORS = ['#9d4edd', '#c77dff', '#e0aaff', '#ffd700', '#ec4899', '#8b5cf6', '#06b6d4'];

  useEffect(() => {
    fetchAnalytics();
  }, [months]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const [moodRes, patternsRes] = await Promise.all([
        axios.get(`${API}/analytics/mood?months=${months}`, { headers, withCredentials: true }),
        axios.get(`${API}/analytics/cycle-patterns`, { headers, withCredentials: true })
      ]);
      
      setMoodData(moodRes.data);
      setCyclePatterns(patternsRes.data);
    } catch (error) {
      // data loads with fallback state
    } finally {
      setLoading(false);
    }
  };

  // Prepare chart data
  const moodChartData = moodData?.mood_distribution 
    ? Object.entries(moodData.mood_distribution).map(([name, value]) => ({ name, value }))
    : [];

  const symptomChartData = moodData?.symptom_frequency
    ? Object.entries(moodData.symptom_frequency).slice(0, 8).map(([name, value]) => ({ name, value }))
    : [];

  const trendChartData = moodData?.weekly_trends?.map(week => ({
    week: week.week_start.slice(5),
    mood: week.dominant_mood || "N/A",
    logs: week.mood_count
  })) || [];

  const cycleHistoryData = cyclePatterns?.cycle_history?.map(c => ({
    date: c.start.slice(5),
    length: c.length
  })) || [];

  const phaseColors = {
    menstrual: "#ef4444",
    follicular: "#ec4899",
    ovulation: "#e879f9",
    luteal: "#8b5cf6"
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-mystic-300" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-mystic-300" />
              Mood Analytics
            </h1>
            <p className="text-mystic-200/70">Understand your emotional patterns</p>
          </div>
          <Select value={months} onValueChange={setMonths}>
            <SelectTrigger className="w-36 bg-mystic-900/50 border-white/10 text-white" data-testid="months-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-mystic-800 border-white/10">
              <SelectItem value="1" className="text-white hover:bg-mystic-700">Last Month</SelectItem>
              <SelectItem value="3" className="text-white hover:bg-mystic-700">Last 3 Months</SelectItem>
              <SelectItem value="6" className="text-white hover:bg-mystic-700">Last 6 Months</SelectItem>
              <SelectItem value="12" className="text-white hover:bg-mystic-700">Last Year</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </motion.div>

      {!moodData?.mood_distribution || Object.keys(moodData.mood_distribution).length === 0 ? (
        <Card className="glass">
          <CardContent className="py-12 text-center">
            <Smile className="w-12 h-12 text-mystic-400 mx-auto mb-4" />
            <p className="text-mystic-200/70">Not enough mood data for analytics.</p>
            <p className="text-mystic-300 text-sm mt-2">
              Log your mood daily in the Calendar to see patterns.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Overview Stats */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <Card className="glass">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white">{moodData?.total_logs || 0}</p>
                <p className="text-mystic-200/70 text-sm">Total Logs</p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white">{Object.keys(moodData?.mood_distribution || {}).length}</p>
                <p className="text-mystic-200/70 text-sm">Moods Tracked</p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white">{Object.keys(moodData?.symptom_frequency || {}).length}</p>
                <p className="text-mystic-200/70 text-sm">Symptoms Logged</p>
              </CardContent>
            </Card>
            <Card className="glass">
              <CardContent className="p-4 text-center">
                <p className="text-3xl font-bold text-white capitalize">
                  {moodChartData[0]?.name || "-"}
                </p>
                <p className="text-mystic-200/70 text-sm">Top Mood</p>
              </CardContent>
            </Card>
          </motion.div>

          {/* Mood Distribution */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
              <Card className="glass h-full">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Smile className="w-5 h-5 text-mystic-300" />
                    Mood Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={moodChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={80}
                          paddingAngle={5}
                          dataKey="value"
                          label={({ name, value }) => `${name}: ${value}%`}
                        >
                          {moodChartData.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a0b2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          itemStyle={{ color: '#f8fafc' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Top Symptoms */}
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
              <Card className="glass h-full">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-cycle-menstrual" />
                    Top Symptoms
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={symptomChartData} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis type="number" stroke="#94a3b8" />
                        <YAxis dataKey="name" type="category" stroke="#94a3b8" width={80} tick={{ fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a0b2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          itemStyle={{ color: '#f8fafc' }}
                        />
                        <Bar dataKey="value" fill="#ec4899" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Phase-Based Mood */}
          {moodData?.phase_moods && Object.keys(moodData.phase_moods).length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Mood by Cycle Phase</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(moodData.phase_moods).map(([phase, data]) => (
                      <div 
                        key={phase} 
                        className="glass-light rounded-xl p-4 text-center"
                        style={{ borderLeft: `3px solid ${phaseColors[phase]}` }}
                      >
                        <p className="text-mystic-300 text-sm capitalize mb-2">{phase}</p>
                        <p className="text-white font-medium capitalize">{data.dominant_mood}</p>
                        <p className="text-mystic-200/50 text-xs">{data.count} entries</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}

          {/* Weekly Trends */}
          {trendChartData.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
              <Card className="glass">
                <CardHeader>
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-mystic-300" />
                    Weekly Log Activity
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={trendChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                        <XAxis dataKey="week" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1a0b2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                          itemStyle={{ color: '#f8fafc' }}
                        />
                        <Line type="monotone" dataKey="logs" stroke="#9d4edd" strokeWidth={2} dot={{ fill: '#9d4edd' }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          )}
        </>
      )}

      {/* Cycle Patterns */}
      {cyclePatterns && !cyclePatterns.message && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}>
          <Card className="glass">
            <CardHeader>
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Calendar className="w-5 h-5 text-cycle-ovulation" />
                Cycle Regularity
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <Badge className={`${
                    cyclePatterns.regularity === "Very Regular" ? "bg-green-500/20 text-green-400" :
                    cyclePatterns.regularity === "Regular" ? "bg-blue-500/20 text-blue-400" :
                    cyclePatterns.regularity === "Somewhat Irregular" ? "bg-yellow-500/20 text-yellow-400" :
                    "bg-red-500/20 text-red-400"
                  } text-sm`}>
                    {cyclePatterns.regularity}
                  </Badge>
                  <p className="text-mystic-200/70 text-sm mt-2">{cyclePatterns.regularity_description}</p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">{cyclePatterns.average_cycle_length} days</p>
                  <p className="text-mystic-200/70 text-sm">Average cycle</p>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="glass-light rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">{cyclePatterns.shortest_cycle}</p>
                  <p className="text-mystic-200/70 text-xs">Shortest</p>
                </div>
                <div className="glass-light rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">{cyclePatterns.longest_cycle}</p>
                  <p className="text-mystic-200/70 text-xs">Longest</p>
                </div>
                <div className="glass-light rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">±{cyclePatterns.standard_deviation}</p>
                  <p className="text-mystic-200/70 text-xs">Variation</p>
                </div>
              </div>

              {cycleHistoryData.length > 0 && (
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={cycleHistoryData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis dataKey="date" stroke="#94a3b8" />
                      <YAxis stroke="#94a3b8" domain={[20, 40]} />
                      <Tooltip 
                        contentStyle={{ backgroundColor: '#1a0b2e', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px' }}
                        itemStyle={{ color: '#f8fafc' }}
                        formatter={(value) => [`${value} days`, 'Cycle Length']}
                      />
                      <Bar dataKey="length" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
};

export default Analytics;
